import { Injectable, computed, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Listing } from 'listings/interfaces/listing.interface';
import { ListingsService } from 'listings/services/listings.service';
import { Observable, catchError, map, of, switchMap } from 'rxjs';
import { ListingStatusEnum } from 'shared/constants/listing-statuses';

@Injectable({
    providedIn: 'root',
})
export class GuardPermissionService {
    listingId = signal<string | null>(null);

    // Convert listing ID signal to observable for API calls
    private listingId$ = toObservable(this.listingId);

    // Signal that contains the current listing data
    private listing = toSignal(
        this.listingId$.pipe(
            switchMap((id) => {
                if (!id) return of(null);
                return this.listingsService.getListingById(id).pipe(catchError(() => of(null)));
            }),
        ),
        { initialValue: null },
    );

    canCreateOutlet = computed(() => {
        const currentListing = this.listing();

        if (!currentListing) {
            return false;
        }

        return this.canCreateOutletCondition(currentListing);
    });

    isLoading = computed(() => {
        const id = this.listingId();
        const listing = this.listing();
        return id !== null && listing === null;
    });

    constructor(
        private listingsService: ListingsService,
        private router: Router,
    ) {}

    canCreateOutletObservable(listingId: string): Observable<boolean> {
        if (!listingId) {
            return of(false);
        }

        return this.listingsService.getListingById(listingId).pipe(
            map((listing: any) => {
                return this.canCreateOutletCondition(listing);
            }),
            catchError(() => of(false)),
        );
    }

    canCreateOutletCondition(listing: Listing): boolean {
        const notAllowedStatuses = [
            ListingStatusEnum.LIVE,
            ListingStatusEnum.PUBLISHING_IN_PROGRESS,
            ListingStatusEnum.APPROVAL,
            ListingStatusEnum.BULK_PROCESSING,
        ];

        return !notAllowedStatuses.includes(listing.listingStatus) && !listing.isReadOnly;
    }
}
